import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { toFarsiDigits } from '../../assets/utils'
import { TOUR_STEPS } from '../../assets/helpContent'

const useStyles = makeStyles(() => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 3000,
  },
  hole: {
    position: 'fixed',
    borderRadius: 12,
    boxShadow: '0 0 0 9999px rgba(10, 14, 40, .62)',
    transition: 'all .3s ease',
    pointerEvents: 'none',
  },
  card: {
    position: 'fixed',
    background: '#fff',
    borderRadius: 14,
    padding: '16px 18px',
    width: 320,
    maxWidth: '92vw',
    maxHeight: 'calc(100vh - 24px)',
    overflowY: 'auto',
    boxShadow: '0 14px 40px rgba(20,30,80,.3)',
    zIndex: 3100,
  },
  title: { fontWeight: 800, fontSize: 15, color: '#1a237e', marginBottom: 6 },
  body: { fontSize: 13, color: '#43476a', lineHeight: 1.9 },
  stepNo: { fontSize: 11, color: '#9aa1b9', fontWeight: 700 },
}))

/**
 * پیدا کردن عنصر هدف — data-tour / سلکتور CSS / «text:متن» برای لینکهای منو
 * اگر منو (روی موبایل داخلِ Drawer است) پیدا نشود ⇒ دکمهٔ «منو» در نوار بالا نشان داده می‌شود.
 */
const findTarget = (target) => {
  if (!target) return null
  if (target === 'nav') return document.querySelector('nav')
  try {
    const el = document.querySelector(`[data-tour="${target}"]`)
    if (el) return el
    const el2 = document.querySelector(target)
    if (el2) return el2
  } catch (e) { /* selector نامعتبر */ }
  return null
}

const findNavByText = (text) => {
  const links = [...document.querySelectorAll('nav a, nav button')]
  return links.find((a) => (a.textContent || '').trim().includes(text)) || null
}

/** هدفِ جایگزین وقتی منو پیدا نشد (موبایل) */
const findMenuToggle = () => document.querySelector('[data-tour="menu-toggle"]')

/**
 * تور آموزشی اولین ورود — کاملاً سفارشی و RTL
 * steps: [{ target: 'css-or-text:...', title, body }]
 * onDone: ذخیرهٔ فلگ پایان
 */
const GuidedTour = ({ steps = [], onDone, onStepChange, initialStep = 0 }) => {
  const classes = useStyles()
  const [idx, setIdx] = useState(initialStep)
  const [rect, setRect] = useState(null)
  const [usedMenu, setUsedMenu] = useState(false)
  const cardRef = useRef(null)
  const [cardH, setCardH] = useState(190)

  const step = steps[idx]

  // ارتفاعِ واقعیِ کارت — تا هیچ‌وقت از پایینِ صفحه بیرون نزند
  useLayoutEffect(() => {
    if (cardRef.current) setCardH(cardRef.current.offsetHeight || 190)
  }, [idx, rect, usedMenu])

  const measure = () => {
    if (!step) return
    let el = null
    const isNavTarget = step.target === 'nav' || (step.target || '').startsWith('text:')
    if (step.target && !step.target.startsWith('text:')) {
      el = findTarget(step.target)
    } else if (step.target && step.target.startsWith('text:')) {
      el = findNavByText(step.target.slice(5))
    }
    // روی موبایل، منو داخلِ Drawer است ⇒ به‌جای منو، دکمهٔ «☰» نوار بالا نشان داده می‌شود
    if (!el && isNavTarget) {
      el = findMenuToggle()
      setUsedMenu(!!el)
    } else {
      setUsedMenu(false)
    }
    if (el) {
      // هدف را داخل دید بیاور (در صفحه‌های بلند، گام‌های پایینی بیرون از صفحه بودند
      // و کارتِ تور هم بیرون از دید می‌افتاد ⇒ دکمه‌های «بعدی/قبلی» دیده نمی‌شد)
      const r0 = el.getBoundingClientRect()
      if (r0.top < 80 || r0.bottom > window.innerHeight - 80) {
        try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }) } catch (e) { /* noop */ }
      }
      const r = el.getBoundingClientRect()
      const pad = 8
      setRect({
        x: Math.max(4, r.x - pad),
        y: Math.max(4, r.y - pad),
        w: Math.min(r.width + pad * 2, window.innerWidth - 8),
        h: Math.min(r.height + pad * 2, window.innerHeight - 8),
      })
    } else {
      setRect(null)
    }
  }

  useEffect(() => {
    measure()
    if (onStepChange) onStepChange(idx)
    const onResize = () => measure()
    window.addEventListener('resize', onResize)
    // با اسکرولِ صفحه (یا اسکرولِ داخلیِ لیست‌ها) حفره باید همراهِ هدف بماند
    window.addEventListener('scroll', onResize, true)
    // راهِ فرار: Esc = رد کردنِ تور (اگر کارت روی موبایل جایی گیر کرد)
    const onKey = (e) => { if (e.key === 'Escape') onDone() }
    window.addEventListener('keydown', onKey)
    // بعضی عنصرها (مثلاً منوی موبایل یا داده‌های صفحه) کمی بعد از رندر ظاهر می‌شوند
    // ⇒ چند بار دیگر اندازه‌گیری می‌کنیم تا حفرهٔ تور درست روی هدف بیفتد.
    const t1 = window.setTimeout(measure, 250)
    const t2 = window.setTimeout(measure, 700)
    const t3 = window.setTimeout(measure, 1400)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onResize, true)
      window.removeEventListener('keydown', onKey)
      window.clearTimeout(t1); window.clearTimeout(t2); window.clearTimeout(t3)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, step])

  // موقعیت کارت: نزدیک هدف یا وسط صفحه — همیشه داخلِ دید (حتی اگر کارت بلند شود)
  const cardPos = useMemo(() => {
    const vh = window.innerHeight
    const maxTop = Math.max(8, vh - cardH - 8)
    if (!rect) {
      return { top: Math.max(8, (vh - cardH) / 2), left: '50%', transform: 'translateX(-50%)' }
    }
    const below = rect.y + rect.h + 14 + cardH < vh
    const left = Math.max(10, Math.min(window.innerWidth - 340, rect.x + rect.w / 2 - 160))
    if (below) return { top: rect.y + rect.h + 14, left }
    if (rect.y > cardH + 24) return { top: Math.max(8, rect.y - 14 - cardH), left }
    return { top: Math.min(maxTop, Math.max(8, (vh - cardH) / 2)), left }
  }, [rect, cardH])

  if (!step) return null

  // روی موبایل، منو داخلِ Drawer است ⇒ یک راهنمای کوتاه اضافه می‌شود
  const bodyText = usedMenu
    ? `${step.body} (روی موبایل، منو را با دکمهٔ ☰ در نوارِ بالا باز کنید.)`
    : step.body

  const next = () => {
    if (idx < steps.length - 1) setIdx(idx + 1)
    else onDone()
  }
  const prev = () => { if (idx > 0) setIdx(idx - 1) }

  return (
    <Box className={classes.overlay} onClick={(e) => e.stopPropagation()}>
      {rect && (
        <Box
          className={classes.hole}
          style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
        />
      )}
      <Box className={classes.card} style={cardPos} ref={cardRef} onClick={(e) => e.stopPropagation()}>
        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={0.5}>
          <Typography className={classes.title}>{step.title}</Typography>
          <Typography className={classes.stepNo}>{`گام ${toFarsiDigits(idx + 1)} از ${toFarsiDigits(steps.length)}`}</Typography>
        </Box>
        <Typography className={classes.body}>{bodyText}</Typography>
        <Box display="flex" justifyContent="space-between" alignItems="center"
          style={{ marginTop: 14, position: 'sticky', bottom: 0, background: '#fff', paddingTop: 6 }}>
          <Button size="small" color="secondary" onClick={onDone} style={{ color: '#9aa1b9' }}>
            رد کردن تور
          </Button>
          <Box display="flex" gap={1}>
            <Button size="small" variant="outlined" disabled={idx === 0} onClick={prev}>قبلی</Button>
            <Button size="small" variant="contained" color="primary" onClick={next}>
              {idx < steps.length - 1 ? 'بعدی' : 'شروع کار 🚀'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default GuidedTour
